/**
 * Electron Renderer - External Scanner Panel
 * 
 * 外部スキャナーモード：チケット管理者以外のPC/デバイスから外部公開のチケットQRをスキャン
 * 許可されたチケットの場合、そのまま入場処理が実行される
 * 
 * 用語:
 * - チケットID: チケットの種類の固有ID（20文字英数字、大文字限定、判定時は大文字小文字区別しない、例: ABCDEFGHIJKLMNOPQRST）
 * - チケットQRコード: チケット一枚一枚の固有ID（8桁英数字、大文字小文字区別しない、例: V0IRA3J2）
 */

import React, { useState, useRef, useEffect } from 'react';
import jsQR from 'jsqr';
import {
  initFirebaseTickets,
  getFirebaseTickets,
  generateDeviceUniqueId,
  processTicketEntry,
  getTicket,
  subscribeToTicket,
  subscribeToQREntries,
  findQRCode,
  loginAnonymously
} from './ticketSystemApi';
import { getTranslation, formatMessage } from './i18n';
import './ExternalScannerPanel.css';

// ========================================
// localStorage 管理
// ========================================
const SCANNED_QRS_KEY = 'externalScanner_scannedQRs';
const SKIP_CONFIRMATION_KEY = 'externalScanner_skipConfirmation';
const FOCUSED_TICKET_KEY = 'externalScanner_focusedTicket';
const QR_SCAN_CONFIRM_ENABLED_KEY = 'externalScanner_qrScanConfirmEnabled';

const getScannedQRs = () => {
  try {
    const data = localStorage.getItem(SCANNED_QRS_KEY);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
};

const saveScannedQR = (qrCode) => {
  try {
    const scanned = getScannedQRs();
    scanned[qrCode] = new Date().toISOString();
    localStorage.setItem(SCANNED_QRS_KEY, JSON.stringify(scanned));
  } catch (error) {
    console.error('Failed to save scanned QR:', error);
  }
};

const isAlreadyScanned = (qrCode) => {
  return getScannedQRs().hasOwnProperty(qrCode);
};

const shouldSkipConfirmation = (qrCode) => {
  try {
    const data = localStorage.getItem(SKIP_CONFIRMATION_KEY);
    return data ? JSON.parse(data)[qrCode] : false;
  } catch {
    return false;
  }
};

const setShouldSkipConfirmation = (qrCode, skip) => {
  try {
    const data = localStorage.getItem(SKIP_CONFIRMATION_KEY) || '{}';
    const obj = JSON.parse(data);
    obj[qrCode] = skip;
    localStorage.setItem(SKIP_CONFIRMATION_KEY, JSON.stringify(obj));
  } catch (error) {
    console.error('Failed to save skip confirmation:', error);
  }
};

const getFocusedTicket = () => {
  try {
    const data = localStorage.getItem(FOCUSED_TICKET_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

const saveFocusedTicket = (ticketId, ticketName) => {
  try {
    localStorage.setItem(FOCUSED_TICKET_KEY, JSON.stringify({
      ticketId,
      ticketName
    }));
  } catch (error) {
    console.error('Failed to save focused ticket:', error);
  }
};

const clearFocusedTicket = () => {
  try {
    localStorage.removeItem(FOCUSED_TICKET_KEY);
  } catch (error) {
    console.error('Failed to clear focused ticket:', error);
  }
};

const getQRScanConfirmEnabled = () => {
  try {
    const savedValue = localStorage.getItem(QR_SCAN_CONFIRM_ENABLED_KEY);
    const isTrue = savedValue === 'true';
    console.log('[ExternalScanner] Getting QR confirmation setting:', {
      savedValue,
      isTrue,
      key: QR_SCAN_CONFIRM_ENABLED_KEY
    });
    return isTrue;
  } catch {
    console.log('[ExternalScanner] Failed to get QR confirmation setting, returning false');
    return false;
  }
};

const saveQRScanConfirmEnabled = (enabled) => {
  try {
    console.log('[ExternalScanner] Saving QR confirmation setting:', {
      enabled,
      stringValue: String(enabled),
      key: QR_SCAN_CONFIRM_ENABLED_KEY
    });
    localStorage.setItem(QR_SCAN_CONFIRM_ENABLED_KEY, String(enabled));
    
    // 保存後に確認
    const savedValue = localStorage.getItem(QR_SCAN_CONFIRM_ENABLED_KEY);
    console.log('[ExternalScanner] Saved value verification:', {
      savedValue,
      isTrue: savedValue === 'true'
    });
  } catch (error) {
    console.error('Failed to save QR scan confirmation setting:', error);
  }
};

const ExternalScannerPanel = ({ onClose, language = 'en' }) => {
  // ========================================
  // State
  // ========================================
  
  const [deviceUniqueId, setDeviceUniqueId] = useState(null);
  const [isProcessingScan, setIsProcessingScan] = useState(false);
  
  // フォーカスモード：チケットID指定でそのチケットに限定
  const [focusedTicketId, setFocusedTicketId] = useState(null);
  const [focusedTicketName, setFocusedTicketName] = useState(null);
  const focusedTicketIdRef = useRef(null);
  const focusedTicketNameRef = useRef(null);
  
  // カメラ関連
  const [useCameraMode, setUseCameraMode] = useState(true);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const lastScannedRef = useRef({ code: '', timestamp: 0 });
  
  // スキャン結果
  const [lastScanResult, setLastScanResult] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  
  // マニュアル入力
  const [manualCode, setManualCode] = useState('');
  const manualInputRef = useRef(null);
  
  // UI状態
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('info');
  
  // 確認ダイアログ
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingQRCode, setPendingQRCode] = useState(null);
  const [pendingQRInfo, setPendingQRInfo] = useState(null);
  const [skipConfirmationCheck, setSkipConfirmationCheck] = useState(false);
  const [enableQRScanConfirmation, setEnableQRScanConfirmation] = useState(false);
  const enableQRScanConfirmationRef = useRef(false);
  
  // チケットID読み込み成功ダイアログ
  const [showTicketIdSuccess, setShowTicketIdSuccess] = useState(false);
  const [ticketIdSuccessInfo, setTicketIdSuccessInfo] = useState(null);
  
  // ========================================
  // 初期化
  // ========================================
  
  useEffect(() => {
    const initScanner = async () => {
      console.log('[ExternalScanner] Initializing...');
      initFirebaseTickets();
      try {
        const deviceId = generateDeviceUniqueId();
        console.log('[ExternalScanner] Device ID generated:', deviceId);
        setDeviceUniqueId(deviceId);
        
        // 匿名認証を自動実行（外部スキャンモードではログイン不要）
        try {
          console.log('[ExternalScanner] Attempting anonymous authentication...');
          await loginAnonymously(deviceId);
          console.log('[ExternalScanner] Anonymous authentication successful');
        } catch (authError) {
          console.warn('[ExternalScanner] Anonymous authentication failed, continuing without auth:', authError);
          // 認証失敗してもスキャナーは動作させる（Firestoreルールで制御）
        }

      // リロード時に選択チケットを復元
      const focused = getFocusedTicket();
      if (focused?.ticketId) {
        console.log('[ExternalScanner] Restored focused ticket:', focused.ticketId);
        setFocusedTicketId(focused.ticketId);
        setFocusedTicketName(focused.ticketName || 'チケット');
        setUseCameraMode(true);
      }

      // QR読み取り確認設定を復元
      const savedQRConfirm = getQRScanConfirmEnabled();
      console.log('[ExternalScanner] Restored QR confirmation setting:', savedQRConfirm);
      setEnableQRScanConfirmation(savedQRConfirm);
      // refも即座に更新（useEffectが実行される前に必要）
      enableQRScanConfirmationRef.current = savedQRConfirm;
      
      // リロード時にスキャン履歴を復元
      try {
        const scannedData = localStorage.getItem(SCANNED_QRS_KEY);
        if (scannedData) {
          const parsed = JSON.parse(scannedData);
          console.log('Restored scanned QRs:', Object.keys(parsed).length, 'items');
        }
      } catch (error) {
        console.warn('Failed to restore scanned QRs:', error);
      }
      
        if (!useCameraMode && manualInputRef.current) {
          manualInputRef.current.focus();
        }
      } catch (error) {
        console.error('Initialize scanner failed:', error);
        showMessage(getTranslation(language, 'initializationFailed'), 'error');
      }
    };
    
    initScanner();
    
    // クリーンアップ
    return () => {
      stopCamera();
    };
  }, []);

  // 選択チケット状態の永続化
  useEffect(() => {
    if (focusedTicketId) {
      saveFocusedTicket(focusedTicketId, focusedTicketName || 'チケット');
    } else {
      clearFocusedTicket();
    }
  }, [focusedTicketId, focusedTicketName]);

  // refの同期
  useEffect(() => {
    focusedTicketIdRef.current = focusedTicketId;
    focusedTicketNameRef.current = focusedTicketName;
  }, [focusedTicketId, focusedTicketName]);

  // リアルタイムリスナー: 選択中のチケット情報を監視
  useEffect(() => {
    if (!focusedTicketId) return;
    
    console.log('[ExternalScanner] Starting realtime listener for ticket:', focusedTicketId);
    
    // チケット情報の監視
    const unsubscribeTicket = subscribeToTicket(focusedTicketId, (updatedTicket) => {
      if (updatedTicket) {
        console.log('[ExternalScanner] Ticket info updated:', updatedTicket);
        // チケット名が変更された場合は更新
        if (updatedTicket.name && updatedTicket.name !== focusedTicketName) {
          setFocusedTicketName(updatedTicket.name);
        }
      } else {
        // チケットが削除された場合
        console.warn('[ExternalScanner] Ticket was deleted');
        showMessage('選択中のチケットが削除されました', 'error');
        setFocusedTicketId(null);
        setFocusedTicketName(null);
      }
    });
    
    // QR入場状態の監視
    const unsubscribeEntries = subscribeToQREntries(focusedTicketId, (event) => {
      console.log('[ExternalScanner] QR entry event:', event.type, event.code);
      
      if (event.type === 'added' && event.entryStatus === 'entered') {
        // 新しい入場があった場合、履歴に追加
        const newHistoryItem = {
          qrCode: event.code,
          ticketId: focusedTicketId,
          ticketName: focusedTicketName,
          status: 'entry_accepted',
          timestamp: new Date(event.enteredAt || Date.now()).toLocaleString('ja-JP'),
          success: true,
          message: '入場しました',
          freeText: event.freeText || ''
        };
        
        setScanHistory(prev => {
          // 重複チェック（同じQRコードが既に履歴にある場合は追加しない）
          const exists = prev.some(item => item.qrCode === event.code);
          if (exists) return prev;
          
          return [newHistoryItem, ...prev].slice(0, 100);
        });
        
        // 通知は表示しない（チケット選択時の混乱を避ける）
        // 履歴には追加するが、メッセージは表示しない
        console.log('[ExternalScanner] QR entry added to history:', event.code);
      }
    });
    
    // クリーンアップ
    return () => {
      console.log('[ExternalScanner] Cleaning up realtime listeners for ticket:', focusedTicketId);
      unsubscribeTicket();
      unsubscribeEntries();
    };
  }, [focusedTicketId, focusedTicketName, deviceUniqueId]);

  // QR読み取り確認設定の永続化
  useEffect(() => {
    console.log('[ExternalScanner] QR confirmation state changed:', enableQRScanConfirmation);
    saveQRScanConfirmEnabled(enableQRScanConfirmation);
  }, [enableQRScanConfirmation]);
  
  // refの同期
  useEffect(() => {
    enableQRScanConfirmationRef.current = enableQRScanConfirmation;
    console.log('[ExternalScanner] QR confirmation ref updated:', enableQRScanConfirmationRef.current);
  }, [enableQRScanConfirmation]);
  
  // カメラモード切り替え時の処理
  useEffect(() => {
    if (useCameraMode) {
      startCamera();
    } else {
      stopCamera();
      if (manualInputRef.current) {
        manualInputRef.current.focus();
      }
    }
  }, [useCameraMode]);
  
  // ========================================
  // カメラ関連
  // ========================================
  
  const startCamera = async () => {
    try {
      setCameraError(null);
      console.log('[ExternalScanner] Starting camera...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        streamRef.current = stream;
        setIsCameraActive(true);
        
        console.log('[ExternalScanner] Camera started, starting QR scan interval...');
        // QRコードスキャンを開始
        scanIntervalRef.current = setInterval(scanQRCode, 500);
      }
    } catch (error) {
      console.error('Camera start failed:', error);
      setCameraError('カメラの起動に失敗しました。カメラへのアクセスを許可してください。');
      setUseCameraMode(false);
    }
  };
  
  const stopCamera = () => {
    // スキャン停止
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    
    // カメラストリーム停止
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setIsCameraActive(false);
  };
  
  const scanQRCode = () => {
    if (!videoRef.current || !canvasRef.current || isProcessingScan) {
      console.log('[ExternalScanner] scanQRCode skipped:', {
        hasVideoRef: !!videoRef.current,
        hasCanvasRef: !!canvasRef.current,
        isProcessingScan
      });
      return;
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    
    console.log('[ExternalScanner] scanQRCode running, video readyState:', video.readyState);
    
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert'
      });
      
      console.log('[ExternalScanner] jsQR result:', code ? 'QR detected' : 'No QR');
      
      if (code && code.data) {
        console.log('[ExternalScanner] QR code detected:', code.data);
        handleCameraQRScanned(code.data);
      }
    }
  };
  
  const handleCameraQRScanned = async (data) => {
    // 同じコードの重複スキャンを防ぐ（3秒以内）
    const now = Date.now();
    if (lastScannedRef.current.code === data && now - lastScannedRef.current.timestamp < 3000) {
      console.log('[ExternalScanner] Duplicate scan prevented:', data);
      return;
    }
    lastScannedRef.current = { code: data, timestamp: now };
    
    console.log('[ExternalScanner] Camera scanned:', data);
    await processQRCode(data);
  };
  
  // ========================================
  // スキャン処理
  // ========================================
  
  const processQRCode = async (qrCode, skipDialog = false) => {
    console.log('[ExternalScanner] processQRCode called:', {
      qrCode,
      isProcessingScan,
      deviceUniqueId,
      skipDialog
    });
    
    if (isProcessingScan) {
      console.log('[ExternalScanner] processQRCode skipped: isProcessingScan is true');
      return;
    }
    
    // deviceUniqueIdがnullの場合、直接生成して状態も更新する
    let effectiveDeviceId = deviceUniqueId;
    if (!effectiveDeviceId) {
      effectiveDeviceId = generateDeviceUniqueId();
      console.log('[ExternalScanner] Generated device ID on demand:', effectiveDeviceId);
      setDeviceUniqueId(effectiveDeviceId);
    }
    if (!effectiveDeviceId) {
      console.log('[ExternalScanner] processQRCode skipped: no device ID');
      return;
    }
    
    try {
      console.log('[ExternalScanner] Setting isProcessingScan to true');
      setIsProcessingScan(true);
      
      // チケットIDか QRコードか判定
      // QRコード: 8桁の英数字（例: W7CFICB6）
      // チケットID: 20文字の英数字（大文字限定、判定時は大文字小文字区別しない）
      const isQRCode = /^[0-9A-Z]{8}$/i.test(qrCode);
      const isTicketId = /^[0-9A-Z]{20}$/i.test(qrCode);
      
      // 入力を大文字に正規化（大文字小文字を区別しない）
      const normalizedCode = qrCode.toUpperCase();
      
      // チケットID形式なら、確認ダイアログを表示
      if (isTicketId) {
        console.log('[ExternalScanner] Ticket ID detected:', normalizedCode);
        try {
          console.log('[ExternalScanner] Calling getTicket...');
          const ticket = await getTicket(normalizedCode);
          console.log('[ExternalScanner] getTicket result:', ticket);
          
          if (ticket) {
            // チケットIDを直接設定（確認ダイアログなし）
            setFocusedTicketId(normalizedCode);
            setFocusedTicketName(ticket.name || 'チケット');
            // refも即座に更新
            focusedTicketIdRef.current = normalizedCode;
            focusedTicketNameRef.current = ticket.name || 'チケット';
            saveFocusedTicket(normalizedCode, ticket.name || 'チケット');
            setUseCameraMode(true);
            
            // チケットID読み取り成功ポップアップを表示
            setTicketIdSuccessInfo({
              ticketId: normalizedCode,
              ticketName: ticket.name || 'チケット',
              message: 'チケットIDよみこみできました、QRを読み取れる状態になりました'
            });
            setShowTicketIdSuccess(true);
            
            console.log('[ExternalScanner] Ticket selected:', ticket.name);
            setIsProcessingScan(false);
            return;
          } else {
            console.log('[ExternalScanner] Ticket not found');
            showMessage('❌ チケットが見つかりません。チケットIDを確認してください。', 'error');
            setIsProcessingScan(false);
            return;
          }
        } catch (error) {
          console.error('[processQRCode] チケット取得エラー:', error);
          console.error('[processQRCode] Error details:', error.message, error.code);
          showMessage('❌ チケット情報の取得に失敗しました。', 'error');
          setIsProcessingScan(false);
          return;
        }
      }
      
      // QRコード形式の場合の処理
      // refを使用して最新の値を取得
      const currentFocusedTicketId = focusedTicketIdRef.current;
      const currentFocusedTicketName = focusedTicketNameRef.current;
      let targetTicketId = currentFocusedTicketId;
      
      console.log('[ExternalScanner] QR Code format check:', {
        qrCode,
        is8Digit: /^[0-9A-Z]{8}$/.test(qrCode),
        focusedTicketId: currentFocusedTicketId,
        focusedTicketName: currentFocusedTicketName,
        focusedTicketIdState: focusedTicketId, // 状態値（比較用）
        focusedTicketIdRef: focusedTicketIdRef.current // ref値（比較用）
      });
      
      // QRコードは必ず8桁の英数字（例: 554Q32AY）
      if (!/^[0-9A-Z]{8}$/.test(normalizedCode)) {
        console.log('[ExternalScanner] Invalid QR code format (not 8-digit alphanumeric)');
        showMessage('❌ QRコード形式が不正です（8桁の英数字である必要があります）', 'error');
        setIsProcessingScan(false);
        return;
      }
      
      // フォーカスモード：選択中のチケットで検証
      if (currentFocusedTicketId) {
        console.log('[ExternalScanner] Focus mode: checking QR code with findQRCode...');
        try {
          const qrData = await findQRCode(currentFocusedTicketId, normalizedCode);
          console.log('[ExternalScanner] findQRCode result:', qrData);
          
          if (!qrData) {
            console.log('[ExternalScanner] QR code not found in ticket');
            showMessage(`❌ このQRは選択中のチケット「${currentFocusedTicketName}」のものではありません`, 'error');
            setIsProcessingScan(false);
            return;
          }
          targetTicketId = currentFocusedTicketId;
        } catch (error) {
          console.error('[processQRCode] QRコード検証エラー:', error);
          showMessage(`❌ QRコードの検証に失敗しました`, 'error');
          setIsProcessingScan(false);
          return;
        }
      } else {
        // フリーモードで8桁QRコード：チケットIDが不明
        console.log('[ExternalScanner] Free mode with 8-digit QR, ticket ID unknown');
        showMessage('❌ まずチケットID（20文字英数字）をスキャンしてください', 'error');
        setIsProcessingScan(false);
        return;
      }
      
      if (!targetTicketId) {
        console.log('[ExternalScanner] Failed to identify ticket ID');
        showMessage('❌ チケットIDを特定できませんでした', 'error');
        setIsProcessingScan(false);
        return;
      }
      
      console.log('[ExternalScanner] Target ticket ID:', targetTicketId);
      
      // QRコード形式で確認設定がONなら、確認ダイアログを表示
      const isScanned = isAlreadyScanned(qrCode);
      const shouldSkip = shouldSkipConfirmation(qrCode);
      const currentEnableQRScanConfirmation = enableQRScanConfirmationRef.current;
      console.log('[ExternalScanner] QR confirmation check:', {
        enableQRScanConfirmation: enableQRScanConfirmation, // state値
        enableQRScanConfirmationRef: currentEnableQRScanConfirmation, // ref値
        skipDialog,
        isScanned,
        shouldSkip,
        shouldShowConfirmation: !skipDialog && currentEnableQRScanConfirmation && !shouldSkip
      });
      
      // 確認ダイアログを表示する条件：
      // 1. skipDialogがfalse（ダイアログをスキップしない）
      // 2. enableQRScanConfirmationがtrue（QR確認設定がON）
      // 3. shouldSkipがfalse（このQRコードで確認をスキップしない）
      if (!skipDialog && currentEnableQRScanConfirmation && !shouldSkip) {
        setPendingQRCode(normalizedCode);
        setPendingQRInfo({
          isScanned,
          qrCode: normalizedCode,
          message: isScanned 
            ? `⚠️ このQRコードは既に読み込み済みです。\nQRコード: ${normalizedCode}\n入場処理を続行しますか？`
            : `確認: このチケットで入場処理を進めますか？\nQRコード: ${normalizedCode}`
        });
        setShowConfirmation(true);
        setIsProcessingScan(false);
        return;
      }
      
      console.log('[ExternalScanner] Processing QR Code:', {
        qrCode,
        targetTicketId,
        effectiveDeviceId,
        focusedTicketName
      });
      
      // 外部スキャナーモードで入場処理
      const result = await processTicketEntry(targetTicketId, normalizedCode, effectiveDeviceId);
      
      console.log('[ExternalScanner] processTicketEntry result:', result);

      // スキャン履歴に保存
      saveScannedQR(normalizedCode);

      const shouldShowResultCard = result.status !== 'ticket_selected';
      if (shouldShowResultCard) {
        setLastScanResult({
          qrCode: normalizedCode,
          result: result,
          timestamp: new Date().toLocaleString('ja-JP'),
          success: result.success
        });
      }
      
      // 履歴に追加
      setScanHistory(prev => [
        {
          qrCode: normalizedCode,
          ticketId: result.ticketId,
          ticketName: result.ticketName,
          status: result.status,
          timestamp: new Date().toLocaleString('ja-JP'),
          success: result.success,
          message: result.message,
          freeText: result.freeText || ''
        },
        ...prev
      ].slice(0, 100)); // 最新100件を保持
      
      // 結果メッセージ表示
      if (result.success) {
        if (result.status === 'ticket_selected') {
          setFocusedTicketId(result.ticketId || null);
          setFocusedTicketName(result.ticketName || null);
          // refも更新
          focusedTicketIdRef.current = result.ticketId || null;
          focusedTicketNameRef.current = result.ticketName || null;
          setUseCameraMode(true);
          showMessage(`✓ ${result.message}`, 'info');
        } else {
          showMessage(`✓ ${result.ticketName}に入場しました`, 'success');
        }
      } else {
        showMessage(`✗ ${result.message}`, 'error');
      }
      
      // 結果を3秒表示
      if (shouldShowResultCard) {
        setTimeout(() => {
          setLastScanResult(null);
        }, 3000);
      }
      
    } catch (error) {
      console.error('Scan processing failed:', error);
      showMessage('処理エラー: ' + error.message, 'error');
    } finally {
      setIsProcessingScan(false);
      // フォーカスを戻す
      if (manualInputRef.current) {
        manualInputRef.current.focus();
      }
    }
  };
  
  const handleManualEntry = async (e) => {
    e.preventDefault();
    
    const code = manualCode.trim();
    if (!code) {
      showMessage('QRコード・チケットIDを入力してください', 'error');
      return;
    }
    
    // チケットIDか QRコードか判定
    // QRコード: 8桁の英数字（例: W7CFICB6）
    // チケットID: 20文字の英数字（大文字限定、判定時は大文字小文字区別しない）
    const isQRCode = /^[0-9A-Z]{8}$/i.test(code);
    const isTicketId = /^[0-9A-Z]{20}$/i.test(code);
    
    if (isTicketId) {
      // チケットID形式：フォーカスモードに入る
      try {
        const ticket = await getTicket(code);
        
        if (ticket) {
          setFocusedTicketId(code);
          setFocusedTicketName(ticket.name || 'チケット');
          // refも即座に更新
          focusedTicketIdRef.current = code;
          focusedTicketNameRef.current = ticket.name || 'チケット';
          saveFocusedTicket(code, ticket.name || 'チケット');
          
          // チケットID読み取り成功ポップアップを表示
          setTicketIdSuccessInfo({
            ticketId: code,
            ticketName: ticket.name || 'チケット',
            message: 'チケットIDよみこみできました、QRを読み取れる状態になりました'
          });
          setShowTicketIdSuccess(true);
          
          setUseCameraMode(true);
          setManualCode('');
        } else {
          showMessage('❌ チケットが見つかりません。チケットIDを確認してください。', 'error');
        }
      } catch (error) {
        console.error('[handleManualEntry] チケット取得エラー:', error);
        console.error('[handleManualEntry] エラー詳細:', error.message, error.code);
        showMessage('❌ チケット情報の取得に失敗しました: ' + (error.message || '不明なエラー'), 'error');
      }
    } else {
      // QRコード形式：直接処理
      setManualCode('');
      await processQRCode(code);
    }
  };
  
  const handlePaste = async (e) => {
    const text = (e.clipboardData || window.clipboardData).getData('text');
    if (text) {
      e.preventDefault();
      setManualCode('');
      await processQRCode(text);
    }
  };
  
  // ========================================
  // 確認ダイアログハンドラー
  // ========================================
  
  const handleConfirmQR = async () => {
    // QRコード確認の場合のみ（チケットIDは直接処理するのでここには来ない）
    // スキップダイアログで続行
    await processQRCode(pendingQRCode, true);
    
    setShowConfirmation(false);
    setSkipConfirmationCheck(false);
    setPendingQRCode(null);
    setPendingQRInfo(null);
  };
  
  const handleCancelQR = () => {
    setShowConfirmation(false);
    setSkipConfirmationCheck(false);
    setPendingQRCode(null);
    setPendingQRInfo(null);
    setIsProcessingScan(false);
    if (manualInputRef.current) {
      manualInputRef.current.focus();
    }
  };
  
  // チケットID読み取り成功ポップアップを閉じる
  const handleCloseTicketIdSuccess = () => {
    setShowTicketIdSuccess(false);
    setTicketIdSuccessInfo(null);
    // カメラモードに戻る
    if (useCameraMode) {
      startCamera();
    }
  };
  
  // ========================================
  // UI ヘルパー
  // ========================================
  
  const showMessage = (msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
    
    // エラーメッセージは5秒間表示
    const timeout = type === 'error' ? 5000 : 3000;
    setTimeout(() => setMessage(null), timeout);
  };
  
  const clearHistory = () => {
    setScanHistory([]);
  };
  
  // ========================================
  // render
  // ========================================
  
  return (
    <div className="external-scanner-container">
      {/* ヘッダー */}
      <div className="scanner-header">
        <h1 className="scanner-title">🔓 外部スキャナーモード</h1>
        <button className="scanner-close-btn" onClick={onClose} title="閉じる">
          ×
        </button>
      </div>
      
      {/* メインコンテンツ */}
      <div className="scanner-content">
        {/* カメラモード */}
        {useCameraMode && (
          <div className="scanner-section">
            <h2>
              {focusedTicketId ? '📷 QRコードを読んでください' : '📷 チケットIDを読んでください'}
              {focusedTicketId && (
                <span className="section-hint">（「{focusedTicketName}」のQRコード）</span>
              )}
            </h2>
            {cameraError ? (
              <div className="camera-error">
                <p>{cameraError}</p>
                <button
                  className="scanner-button primary"
                  onClick={() => setUseCameraMode(true)}
                >
                  再試行
                </button>
              </div>
            ) : (
              <div className="camera-container">
                <video
                  ref={videoRef}
                  className="camera-video"
                  playsInline
                  muted
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                {isCameraActive && !isProcessingScan && (
                  <div className="camera-overlay">
                    <p className="scan-hint">
                      {focusedTicketId 
                        ? `「${focusedTicketName}」のQRコード（8桁英数字）をスキャンしてください`
                        : 'チケットID（20文字英数字）をスキャンしてください'
                      }
                    </p>
                    {message && messageType === 'error' && (
                      <div className="camera-error-message">
                        {message}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* 現在のモード・チケット状態表示（カメラ画面の下） */}
        {useCameraMode && (
          <div className="scanner-section scanner-status">
            <div className="status-item">
              <label className="scan-confirm-toggle">
                <input
                  type="checkbox"
                  checked={enableQRScanConfirmation}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    console.log('[ExternalScanner] QR confirmation checkbox changed:', {
                      newValue,
                      currentState: enableQRScanConfirmation,
                      eventTargetChecked: e.target.checked,
                      eventTargetValue: e.target.value
                    });
                    setEnableQRScanConfirmation(newValue);
                  }}
                />
                QR読み込み時確認をする
              </label>
            </div>
            
            {focusedTicketId && (
              <div className="status-item focused-ticket">
                <span className="status-label">【選択チケット】</span>
                <span className="status-value">
                  {focusedTicketName}
                  <button
                    className="clear-ticket-btn"
                    onClick={() => {
                      setFocusedTicketId(null);
                      setFocusedTicketName(null);
                      // refもクリア
                      focusedTicketIdRef.current = null;
                      focusedTicketNameRef.current = null;
                      clearFocusedTicket();
                      setUseCameraMode(false);
                      if (manualInputRef.current) manualInputRef.current.focus();
                    }}
                    title="チケット選択をクリア"
                  >
                    ✕
                  </button>
                </span>
              </div>
            )}
          </div>
        )}
        
        {/* 手動入力モード */}
        {!useCameraMode && (
          <div className="scanner-section">
            <h2>
              {focusedTicketId 
                ? `⌨️ 「${focusedTicketName}」のQRコードを入力してください`
                : '⌨️ チケットIDを入力してください'
              }
            </h2>
            <form onSubmit={handleManualEntry} className="scanner-form">
              <div className="input-group">
                <input
                  ref={manualInputRef}
                  type="text"
                  className="scanner-input"
                  placeholder={
                    focusedTicketId
                      ? `QRコード（8桁英数字、例: W7CFICB6）を入力`
                      : 'チケットID（20文字英数字、例: ABCDEFGHIJKLMNOPQRST）を入力'
                  }
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  onPaste={handlePaste}
                  disabled={isProcessingScan}
                  autoFocus
                />
                <button
                  type="submit"
                  className="scanner-button primary"
                  disabled={isProcessingScan || !manualCode.trim()}
                >
                  {isProcessingScan ? '処理中...' : '実行'}
                </button>
              </div>
            </form>
          </div>
        )}
        
        {/* スキャン結果 */}
        {lastScanResult && (
          <div className="scanner-section">
            <div className={`scanner-result ${lastScanResult.success ? 'success' : 'error'}`}>
              <div className="result-icon">
                {lastScanResult.success ? '✓' : '✗'}
              </div>
              <div className="result-content">
                <div className="result-status">
                  {lastScanResult.success ? '成功' : '失敗'}
                </div>
                <div className="result-message">
                  {lastScanResult.result.message}
                </div>
                {lastScanResult.result.ticketName && (
                  <div className="result-ticket">
                    {lastScanResult.result.ticketName}
                  </div>
                )}
                {!lastScanResult.success && lastScanResult.qrCode && (
                  <div className="result-qr-code">
                    読み取った値: {lastScanResult.qrCode.substring(0, 50)}
                    {lastScanResult.qrCode.length > 50 ? '...' : ''}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* メッセージ */}
        {message && (
          <div className={`scanner-message ${messageType}`}>
            {message}
          </div>
        )}
        
        {/* モード切り替え */}
        <div className="scanner-section">
          <div className="mode-switcher">
            <button
              className={`mode-button ${useCameraMode ? 'active' : ''}`}
              onClick={() => setUseCameraMode(true)}
              disabled={isProcessingScan}
            >
              📷 カメラスキャン
            </button>
            <button
              className={`mode-button ${!useCameraMode ? 'active' : ''}`}
              onClick={() => setUseCameraMode(false)}
              disabled={isProcessingScan}
            >
              ⌨️ 手動入力
            </button>
          </div>
        </div>
        
        {/* 履歴セクション */}
        <div className="scanner-section">
          <div className="section-header">
            <h2>スキャン履歴</h2>
            <div className="history-actions">
              <button
                className="history-toggle"
                onClick={() => setShowHistory(!showHistory)}
              >
                {showHistory ? '▼' : '▶'} {scanHistory.length}件
              </button>
              {scanHistory.length > 0 && (
                <button
                  className="history-clear"
                  onClick={clearHistory}
                  title="履歴をクリア"
                >
                  🗑️
                </button>
              )}
            </div>
          </div>
          
          {showHistory && (
            <div className="history-list">
              {/* 検索フィルター */}
              <div className="history-search" style={{ marginBottom: '10px', padding: '10px', background: '#f5f5f5', borderRadius: '4px' }}>
                <input
                  type="text"
                  placeholder="🔍 QRコード、座席番号、フリー入力で検索..."
                  value={historySearchQuery}
                  onChange={(e) => setHistorySearchQuery(e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              {scanHistory.filter(item => {
                if (!historySearchQuery) return true;
                const query = historySearchQuery.toLowerCase();
                return (
                  item.qrCode?.toLowerCase().includes(query) ||
                  item.freeText?.toLowerCase().includes(query) ||
                  item.message?.toLowerCase().includes(query) ||
                  item.ticketName?.toLowerCase().includes(query)
                );
              }).length === 0 ? (
                <div className="empty-history">
                  {historySearchQuery ? '検索結果がありません' : 'スキャン履歴がありません'}
                </div>
              ) : (
                scanHistory.filter(item => {
                  if (!historySearchQuery) return true;
                  const query = historySearchQuery.toLowerCase();
                  return (
                    item.qrCode?.toLowerCase().includes(query) ||
                    item.freeText?.toLowerCase().includes(query) ||
                    item.message?.toLowerCase().includes(query) ||
                    item.ticketName?.toLowerCase().includes(query)
                  );
                }).map((item, index) => (
                  <div
                    key={index}
                    className={`history-item ${item.success ? 'success' : 'error'}`}
                  >
                    <div className="history-item-header">
                      <span className="history-time">{item.timestamp}</span>
                      <span className={`history-status ${item.success ? 'success' : 'error'}`}>
                        {item.success ? '✓' : '✗'} {item.status}
                      </span>
                    </div>
                    {item.qrCode && (
                      <div className="history-qrcode" style={{ color: '#666', fontSize: '0.9em', margin: '4px 0' }}>
                        🎫 QR: {item.qrCode}
                      </div>
                    )}
                    {item.ticketName && (
                      <div className="history-ticket">{item.ticketName}</div>
                    )}
                    {item.freeText && (
                      <div className="history-freetext" style={{ color: '#0066cc', fontWeight: 'bold', margin: '4px 0' }}>
                        📍 {item.freeText}
                      </div>
                    )}
                    <div className="history-message">{item.message}</div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* フッター */}
      <div className="scanner-footer">
      </div>
      
      {/* 確認ダイアログ */}
      {showConfirmation && (
        <div className="confirmation-modal-overlay">
          <div className="confirmation-modal">
            <div className="confirmation-header">
              <h2>{pendingQRInfo?.isTicketId ? 'チケットID 確認' : '確認'}</h2>
            </div>
            <div className="confirmation-body">
              <p>{pendingQRInfo?.message}</p>
              {pendingQRInfo?.isScanned && (
                <div className="already-scanned-warning">
                  ⚠️ 既に読み込み済みです
                </div>
              )}
            </div>
            <div className="confirmation-footer">
              <button
                className="confirmation-button cancel"
                onClick={handleCancelQR}
              >
                キャンセル
              </button>
              <button
                className="confirmation-button confirm"
                onClick={handleConfirmQR}
              >
                続行
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* チケットID読み取り成功ポップアップ */}
      {showTicketIdSuccess && ticketIdSuccessInfo && (
        <div className="confirmation-modal-overlay">
          <div className="confirmation-modal success-modal">
            <div className="confirmation-header success-header">
              <h2>🎉 チケットID読み取り成功</h2>
            </div>
            <div className="confirmation-body success-body">
              <div className="success-icon">✓</div>
              <p className="success-message">{ticketIdSuccessInfo.message}</p>
              <div className="ticket-info">
                <div className="ticket-info-item">
                  <span className="ticket-info-label">チケットID:</span>
                  <span className="ticket-info-value">{ticketIdSuccessInfo.ticketId}</span>
                </div>
                <div className="ticket-info-item">
                  <span className="ticket-info-label">チケット名:</span>
                  <span className="ticket-info-value">{ticketIdSuccessInfo.ticketName}</span>
                </div>
              </div>
              <p className="success-hint">
                これでQRコード（8桁英数字）をスキャンできる状態になりました。
              </p>
            </div>
            <div className="confirmation-footer success-footer">
              <button
                className="confirmation-button confirm success-button"
                onClick={handleCloseTicketIdSuccess}
              >
                了解、QRコードをスキャンします
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExternalScannerPanel;
